/* eslint-disable react-refresh/only-export-components */
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export type CompanyRole = "owner" | "admin" | "editor" | "viewer";

export type CompanyMembership = {
  company_id: string;
  user_id: string;
  role: CompanyRole;
  joined_at: string;
  companies: {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    is_platform_owner: boolean;
    owner_user_id: string | null;
    subscription_plan_id: string | null;
    created_at: string;
  };
};

type CompanyContextValue = {
  memberships: CompanyMembership[];
  currentCompany: CompanyMembership["companies"] | null;
  currentMembership: CompanyMembership | null;
  currentCompanyId: string | null;
  role: CompanyRole | null;
  isPlatformOwner: boolean;
  isLoading: boolean;
  setCurrentCompanyId: (companyId: string) => void;
};

const CompanyContext = createContext<CompanyContextValue>({
  memberships: [],
  currentCompany: null,
  currentMembership: null,
  currentCompanyId: null,
  role: null,
  isPlatformOwner: false,
  isLoading: true,
  setCurrentCompanyId: () => {},
});

type DbError = { message: string };
type DbResult<T = unknown> = { data: T | null; error: DbError | null };
type LooseQuery<T = unknown> = PromiseLike<DbResult<T>> & {
  select: (columns?: string) => LooseQuery<T>;
  eq: (column: string, value: unknown) => LooseQuery<T>;
  order: (column: string, options?: { ascending?: boolean }) => LooseQuery<T>;
};
type LooseSupabaseClient = { from: (table: string) => LooseQuery; rpc: (fn: string, args?: Record<string, unknown>) => PromiseLike<DbResult> };

const companyDb = supabase as unknown as LooseSupabaseClient;

async function fetchMemberships(userId: string): Promise<CompanyMembership[]> {
  const { data, error } = await companyDb
    .from("company_members")
    .select("company_id,user_id,role,joined_at,companies(id,name,slug,logo_url,is_platform_owner,owner_user_id,subscription_plan_id,created_at)")
    .eq("user_id", userId)
    .order("joined_at", { ascending: true });

  if (error) throw error;
  return (data ?? []) as CompanyMembership[];
}

async function fetchAllCompanies(): Promise<CompanyMembership["companies"][]> {
  const { data, error } = await companyDb
    .from("companies")
    .select("id,name,slug,logo_url,is_platform_owner,owner_user_id,subscription_plan_id,created_at")
    .order("name", { ascending: true });

  if (error) throw error;
  return (data ?? []) as CompanyMembership["companies"][];
}

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currentCompanyId, setCurrentCompanyIdState] = useState<string | null>(() =>
    localStorage.getItem("current_company_id"),
  );

  const { data: memberships = [], isLoading } = useQuery({
    queryKey: ["company-memberships", user?.id],
    queryFn: () => fetchMemberships(user!.id),
    enabled: !!user,
  });

  const { data: platformOwner = false, isLoading: platformLoading } = useQuery({
    queryKey: ["company-platform-owner", user?.id],
    queryFn: async () => {
      const { data, error } = await companyDb.rpc("is_platform_owner", { _user: user!.id });
      if (error) throw error;
      return Boolean(data);
    },
    enabled: !!user,
  });

  const { data: allCompanies = [], isLoading: allCompaniesLoading } = useQuery({
    queryKey: ["company-admin-all-companies", user?.id, platformOwner],
    queryFn: fetchAllCompanies,
    enabled: !!user && platformOwner,
  });

  const visibleMemberships = useMemo(
    () => {
      if (!platformOwner) return memberships.filter((membership) => !membership.companies?.is_platform_owner);
      const byCompany = new Map(memberships.map((membership) => [membership.company_id, membership]));
      for (const company of allCompanies) {
        if (!byCompany.has(company.id)) {
          byCompany.set(company.id, {
            company_id: company.id,
            user_id: user?.id ?? "",
            role: "admin",
            joined_at: company.created_at,
            companies: company,
          });
        }
      }
      return Array.from(byCompany.values()).sort((a, b) => a.companies.name.localeCompare(b.companies.name));
    },
    [allCompanies, memberships, platformOwner, user?.id],
  );

  const currentMembership = useMemo(() => {
    if (!visibleMemberships.length) return null;
    return visibleMemberships.find((membership) => membership.company_id === currentCompanyId) ?? visibleMemberships[0];
  }, [currentCompanyId, visibleMemberships]);

  useEffect(() => {
    if (!currentMembership) return;
    if (currentMembership.company_id !== currentCompanyId) {
      setCurrentCompanyIdState(currentMembership.company_id);
    }
    localStorage.setItem("current_company_id", currentMembership.company_id);
  }, [currentCompanyId, currentMembership]);

  useEffect(() => {
    if (!user) {
      setCurrentCompanyIdState(null);
      localStorage.removeItem("current_company_id");
    }
  }, [user]);

  const value = useMemo<CompanyContextValue>(() => {
    return {
      memberships: visibleMemberships,
      currentCompany: currentMembership?.companies ?? null,
      currentMembership,
      currentCompanyId: currentMembership?.company_id ?? null,
      role: currentMembership?.role ?? null,
      isPlatformOwner: platformOwner,
      isLoading: isLoading || platformLoading || (platformOwner && allCompaniesLoading),
      setCurrentCompanyId: (companyId: string) => {
        setCurrentCompanyIdState(companyId);
        localStorage.setItem("current_company_id", companyId);
      },
    };
  }, [allCompaniesLoading, currentMembership, isLoading, platformLoading, platformOwner, visibleMemberships]);

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

export const useCompany = () => useContext(CompanyContext);
